<template>
  <Modal ref="modalRef" width="40rem">
    <template #body>
      <View gap="xl" direction="column">
        <View gap="md" direction="column" align="center">
          <Text variant="h2">Disconnect Google Calendar?</Text>
          <Text variant="body-large" color="secondary" align="center">
            Are you sure you want to disconnect your Google Calendar?
          </Text>
        </View>

        <WhatToExpectCard :items="whatToExpectNextItems" />
      </View>
    </template>

    <template #footer>
      <View gap="md" :grow="1" class="actions">
        <Button variant="secondary" @click="close">Cancel</Button>
        <Button variant="primary" @click="disconnect" :loading="isLoading">Disconnect</Button>
      </View>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { ref } from 'vue';

import { googleCalendarService, CalendarProvider } from '@tallo/auth';
import { Button, Modal, Text, View, WhatToExpectCard, WhatToExpectItem, useAlertsStore } from '@tallo/design-system';
import { useInvestorStore } from '@tallo/investor/investor';

const emit = defineEmits(['disconnected']);
const modalRef = ref<InstanceType<typeof Modal>>();
const alertsStore = useAlertsStore();
const investorStore = useInvestorStore();
const isLoading = ref(false);

const whatToExpectNextItems: WhatToExpectItem[] = [
  {
    icon: 'x-close-circle',
    title: 'Stop syncing new showings',
    description: 'New showings will no longer be added to your Google Calendar.',
  },
  {
    icon: 'x-close-circle',
    title: "Remove Tallo's access",
    description: 'Tallo will no longer have access to your Google Calendar.',
  },
  {
    icon: 'x-close-circle',
    title: 'Delete Tallo calendar',
    description: 'The Tallo calendar in your Google account will be deleted.',
  },
];

async function disconnect() {
  isLoading.value = true;
  try {
    await googleCalendarService.disconnect(CalendarProvider.GOOGLE);

    await investorStore.load();

    close();
    alertsStore.success('Google Calendar disconnected successfully');
    emit('disconnected');
  } catch (error) {
    alertsStore.error('Failed to disconnect Google Calendar');
  } finally {
    isLoading.value = false;
  }
}

function close() {
  modalRef.value?.close();
}

defineExpose({
  modalRef,
});
</script>

<style scoped lang="scss">
.actions > * {
  width: 100%;
}
</style>
